import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PageHeader, Table, BadgePill, EmptyState } from '@/components/campus';
import {
  CreditCard,
  Calendar,
  Receipt,
  DollarSign,
  Ticket,
  Megaphone,
  ArrowUpRight,
  Download,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Sparkles,
  Building2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan configuration
const planConfig = {
  free: { 
    name: 'Free', 
    color: 'text-soft-silver',
    bg: 'bg-soft-silver/10',
    border: 'border-soft-silver/20'
  },
  starter: { 
    name: 'Starter', 
    color: 'text-electric-ice-blue',
    bg: 'bg-electric-ice-blue/10',
    border: 'border-electric-ice-blue/20'
  },
  pro: { 
    name: 'Pro', 
    color: 'text-cool-mint',
    bg: 'bg-cool-mint/10',
    border: 'border-cool-mint/20'
  },
  enterprise: { 
    name: 'Enterprise', 
    color: 'text-soft-amber',
    bg: 'bg-soft-amber/10',
    border: 'border-soft-amber/20'
  },
};

// Status configuration
const statusConfig = {
  active: { label: 'Active', variant: 'success', icon: CheckCircle },
  trialing: { label: 'Trial', variant: 'primary', icon: Clock },
  past_due: { label: 'Past Due', variant: 'error', icon: AlertTriangle },
  canceled: { label: 'Canceled', variant: 'default', icon: AlertTriangle },
};

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

// ==================== CURRENT PLAN CARD ====================
const CurrentPlanCard = ({ subscription, onUpgrade }) => {
  const plan = planConfig[subscription?.plan] || planConfig.free;
  const status = statusConfig[subscription?.status] || statusConfig.active;
  const StatusIcon = status.icon;

  return (
    <div 
      className={cn(
        "p-6 rounded-2xl border bg-gradient-to-br from-graphite to-obsidian",
        plan.border
      )}
      data-testid="current-plan-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", plan.bg)}>
            <Sparkles className={cn("w-6 h-6", plan.color)} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-cloud-white">{plan.name} Plan</h3>
            <div className="flex items-center gap-2 mt-1">
              <BadgePill variant={status.variant} size="sm">
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </BadgePill>
            </div>
          </div>
        </div>
        
        {subscription?.plan !== 'enterprise' && (
          <Button
            onClick={onUpgrade}
            className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            data-testid="upgrade-btn"
          >
            <ArrowUpRight className="w-4 h-4 mr-2" />
            Upgrade
          </Button>
        )}
      </div>

      {subscription?.plan === 'free' && (
        <div className="p-3 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/10 mt-4">
          <p className="text-sm text-soft-silver">
            <span className="text-electric-ice-blue font-medium">Upgrade to Starter</span> for more events, 
            guests, and advanced features.
          </p>
        </div>
      )}
    </div>
  );
};

// ==================== SUBSCRIPTION DETAILS ====================
const SubscriptionDetails = ({ subscription }) => {
  return (
    <div className="p-6 bg-graphite/50 rounded-2xl border border-white/[0.06]" data-testid="subscription-details">
      <h3 className="font-semibold text-cloud-white mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-electric-ice-blue" />
        Subscription Details
      </h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
          <span className="text-soft-silver">Billing Cycle</span>
          <span className="text-cloud-white font-medium capitalize">
            {subscription?.billing_cycle || 'Monthly'}
          </span>
        </div>
        
        <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
          <span className="text-soft-silver">Current Period</span>
          <span className="text-cloud-white font-mono text-sm">
            {formatDate(subscription?.current_period_start)} — {formatDate(subscription?.current_period_end)}
          </span>
        </div>
        
        <div className="flex items-center justify-between py-3">
          <span className="text-soft-silver">Payment Method</span>
          {subscription?.payment_method ? (
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-muted-text" />
              <span className="text-cloud-white font-mono">•••• {subscription.payment_method.last4}</span>
            </div>
          ) : (
            <span className="text-muted-text italic">No payment method on file</span>
          )}
        </div>
      </div>

      {!subscription?.payment_method && subscription?.plan !== 'free' && (
        <Button
          variant="outline"
          className="w-full mt-4 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          data-testid="add-payment-btn"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Add Payment Method
        </Button>
      )}
    </div>
  );
};

// ==================== REVENUE SUMMARY CARDS ====================
const RevenueSummaryCards = ({ revenue }) => {
  const items = [
    { 
      label: 'Ticket Revenue', 
      value: revenue?.ticket_revenue || 0, 
      icon: Ticket, 
      color: 'electric-ice-blue',
      description: 'From event ticket sales'
    },
    { 
      label: 'Sponsor Revenue', 
      value: revenue?.sponsor_revenue || 0, 
      icon: Megaphone, 
      color: 'cool-mint',
      description: 'From sponsorships & ads'
    },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="revenue-summary">
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <div 
            key={index}
            className="p-6 bg-graphite/50 rounded-2xl border border-white/[0.06]"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center",
                `bg-${item.color}/10`
              )}>
                <Icon className={cn("w-5 h-5", `text-${item.color}`)} />
              </div>
              <div>
                <p className="text-sm text-muted-text">{item.label}</p>
                <p className="text-xs text-muted-text">{item.description}</p>
              </div>
            </div>
            <p className="text-2xl font-bold text-cloud-white font-mono">
              {formatCurrency(item.value, revenue?.currency)}
            </p>
            {item.value === 0 && (
              <p className="text-xs text-muted-text mt-2 italic">No revenue recorded yet</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== INVOICE HISTORY ====================
const InvoiceHistory = ({ invoices, loading }) => {
  const columns = [
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <span className="font-mono text-sm text-cloud-white">{formatDate(value)}</span>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => <span className="text-soft-silver">{value}</span>,
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      render: (value, row) => (
        <span className="font-mono text-cloud-white">{formatCurrency(value, row.currency)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => {
        const statusMap = {
          paid: { label: 'Paid', variant: 'success' },
          pending: { label: 'Pending', variant: 'warning' },
          failed: { label: 'Failed', variant: 'error' },
        };
        const config = statusMap[value] || statusMap.pending;
        return <BadgePill variant={config.variant}>{config.label}</BadgePill>;
      },
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-text hover:text-cloud-white"
          disabled={!row.invoice_url}
        >
          <Download className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] overflow-hidden" data-testid="invoice-history">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <Receipt className="w-5 h-5 text-soft-amber" />
          Invoice History
        </h3>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin mx-auto" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="p-8">
          <EmptyState
            icon="receipt"
            title="No invoices yet"
            description="Your invoice history will appear here once you have paid subscriptions."
          />
        </div>
      ) : (
        <Table columns={columns} data={invoices} />
      )}
    </div>
  );
};

// ==================== UPGRADE MODAL PLACEHOLDER ====================
const UpgradeNotice = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/80 backdrop-blur-sm">
      <div className="bg-graphite rounded-2xl border border-white/[0.08] p-6 max-w-md mx-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-electric-ice-blue/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-electric-ice-blue" />
          </div>
          <h2 className="text-xl font-bold text-cloud-white mb-2">Upgrade Coming Soon</h2>
          <p className="text-soft-silver mb-6">
            Stripe payment integration is being set up. You'll be able to upgrade your plan soon!
          </p>
          <Button
            onClick={onClose}
            className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN BILLING PAGE ====================
export const Billing = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();

  // State
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check permissions
  const canManage = hasOrgRole(['owner', 'admin']);

  // Fetch billing data
  const fetchBilling = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/billing?organization_id=${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to load billing info');
      }
    } catch (error) {
      console.error('Failed to fetch billing:', error);
      toast.error('Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading billing info...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Page Header */}
      <PageHeader
        title="Billing"
        subtitle={billing?.organization?.name}
      />

      {/* Current Plan & Subscription Details */}
      <div className="grid lg:grid-cols-2 gap-6">
        <CurrentPlanCard 
          subscription={billing?.subscription} 
          onUpgrade={handleUpgrade}
        />
        <SubscriptionDetails subscription={billing?.subscription} />
      </div>

      {/* Revenue Summary */}
      <div>
        <h2 className="text-lg font-semibold text-cloud-white mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-cool-mint" />
          Revenue Summary
        </h2>
        <RevenueSummaryCards revenue={billing?.revenue} />
      </div>

      {/* Invoice History */}
      <InvoiceHistory invoices={billing?.invoices || []} loading={loading} />

      {/* Upgrade Modal */}
      <UpgradeNotice 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
};

export default Billing;
